import { useState } from 'react';
import Breadcrumb from '@/components/base/Breadcrumb';
import { platforms, articles } from '@/mocks/data';
import { Link } from 'react-router-dom';

export default function AboutPage() {
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      setFormMessage('感谢您的留言！我们会尽快回复。');
      setContactForm({ name: '', email: '', message: '' });
      return;
    }
    formData.delete('phone_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9n1dg4it35ii3jdlnag', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言！我们会尽快回复。');
        setContactForm({ name: '', email: '', message: '' });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormStatus('error');
        setFormMessage(serverMsg.includes('spam') ? '提交失败，请稍后重试。' : (serverMsg || '提交失败，请稍后重试。'));
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后重试。');
    }
  };

  return (
    <main className="min-h-screen bg-background-50">
      {/* SEO Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify([
            {
              '@context': 'https://schema.org',
              '@type': 'AboutPage',
              name: '关于榴莲视频',
              description: '榴莲视频是一个专业的在线视频平台导航和评测网站，致力于帮助用户找到最适合的在线影视平台。',
              url: 'https://durian-video.example.com/about',
            },
            {
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://durian-video.example.com/' },
                { '@type': 'ListItem', position: 2, name: '关于我们' },
              ],
            },
          ]),
        }}
      />

      {/* Hero */}
      <section className="relative w-full min-h-[400px] md:min-h-[480px] flex items-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Warm%20inviting%20modern%20office%20workspace%20with%20plants%20and%20natural%20wood%20elements%2C%20soft%20golden%20hour%20light%2C%20creative%20team%20atmosphere%2C%20no%20people%20visible%2C%20minimal%20aesthetic%2C%20warm%20coral%20and%20cream%20tones&width=1800&height=700&seq=about-hero&orientation=landscape"
          alt="关于榴莲视频"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/25 to-black/45"></div>
        <div className="relative z-10 w-full px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '关于我们' },
              ]}
            />
            <div className="flex justify-center mb-4">
              <span className="inline-flex items-center justify-center" />
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white mb-4">
              关于榴莲视频
            </h1>
            <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto leading-relaxed">
              您值得信赖的在线视频平台导航与评测专家
            </p>
          </div>
        </div>
      </section>

      {/* Brand Introduction */}
      <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center gap-10">
            <div className="flex-1">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-6">
                品牌介绍
              </h2>
              <div className="space-y-4">
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  榴莲视频成立于2023年，是一个专注于<strong>在线视频平台导航与评测</strong>的专业内容网站。我们深知在众多视频平台中选择最适合自己的那一个并不容易，因此致力于为用户提供全面、客观、专业的平台评测和对比分析。
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  我们的团队由资深影视爱好者和技术专家组成，覆盖<strong>97视频、丝瓜视频、蓝莓视频、91在线视频、糖心视频、黑桃视频</strong>等主流在线视频平台，从内容质量、播放体验、会员价格、界面设计等多个维度进行深度评测。
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  无论是寻找高清影视资源，还是对比不同平台的会员权益，榴莲视频都能为您提供最有价值的参考信息。我们坚持<strong>客观公正</strong>的评测原则，帮助每一位用户做出明智的选择。
                </p>
              </div>
            </div>
            <div className="flex-shrink-0">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20minimal%20creative%20workspace%20with%20computer%20screen%20displaying%20video%20content%2C%20warm%20wooden%20desk%2C%20small%20plant%2C%20natural%20light%2C%20cozy%20atmosphere%2C%20no%20people%2C%20editorial%20photography%20style&width=500&height=500&seq=about-brand&orientation=squarish"
                alt="榴莲视频品牌"
                className="w-full max-w-xs lg:max-w-sm rounded-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { number: '7+', label: '覆盖平台' },
              { number: '50+', label: '评测文章' },
              { number: '10万+', label: '月访问量' },
              { number: '98%', label: '用户好评率' },
            ].map((stat, index) => (
              <div key={index}>
                <div className="text-3xl md:text-4xl font-heading font-bold text-accent-600 mb-2">
                  {stat.number}
                </div>
                <div className="text-sm text-foreground-500">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reputation / Testimonials */}
      <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
              用户口碑
            </h2>
            <p className="text-sm text-foreground-500">听听用户们怎么说</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              {
                name: '王先生',
                role: '影视爱好者',
                comment: '榴莲视频的评测文章写得很专业，帮我找到了最适合的视频平台。特别是那个五大平台横向对比，非常实用！',
                rating: 5,
              },
              {
                name: '李女士',
                role: '自由职业者',
                comment: '之前一直纠结选哪个视频平台，看了榴莲视频的对比分析后，果断选择了蓝莓视频，推荐算法真的很好用。',
                rating: 5,
              },
              {
                name: '张同学',
                role: '大学生',
                comment: '作为学生党，预算有限，榴莲视频帮我找到了性价比最高的91在线视频，省了不少钱。感谢！',
                rating: 5,
              },
            ].map((testimonial, index) => (
              <div
                key={index}
                className="bg-background-50 border border-background-200/60 rounded-lg p-5"
              >
                <div className="flex items-center gap-1 mb-3 text-secondary-500">
                  {Array.from({ length: testimonial.rating }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-xs"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  "{testimonial.comment}"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-primary-100 rounded-full">
                    <span className="text-xs font-semibold text-primary-600">
                      {testimonial.name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-900">{testimonial.name}</div>
                    <div className="text-xs text-foreground-400">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100" id="contact">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
              联系我们
            </h2>
            <p className="text-sm text-foreground-500">有任何问题或建议，欢迎给我们留言</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
            {[
              {
                icon: 'ri-mail-line',
                title: '邮箱',
                content: 'contact@durian-video.example.com',
              },
              {
                icon: 'ri-wechat-line',
                title: '微信',
                content: '榴莲视频官方',
              },
              {
                icon: 'ri-time-line',
                title: '工作时间',
                content: '周一至周五 9:00-18:00',
              },
            ].map((info, index) => (
              <div
                key={index}
                className="flex flex-col items-center text-center p-5 bg-background-50 rounded-lg border border-background-200/60"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-accent-100 rounded-full mb-3">
                  <i className={`${info.icon} text-accent-600`}></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1">{info.title}</h4>
                <p className="text-xs text-foreground-500">{info.content}</p>
              </div>
            ))}
          </div>

          <form
            onSubmit={handleContactSubmit}
            data-readdy-form
            id="contact-form"
            className="bg-background-50 rounded-lg border border-background-200/60 p-6"
          >
            <input
              type="text"
              name="phone_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="honeypot-field"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  姓名
                </label>
                <input
                  id="contact-name"
                  type="text"
                  name="name"
                  value={contactForm.name}
                  onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                  required
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 placeholder:text-foreground-400"
                  placeholder="请输入您的姓名"
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  邮箱
                </label>
                <input
                  id="contact-email"
                  type="email"
                  name="email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  required
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 placeholder:text-foreground-400"
                  placeholder="请输入您的邮箱"
                />
              </div>
            </div>
            <div className="mb-4">
              <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                留言内容
              </label>
              <textarea
                id="contact-message"
                name="message"
                value={contactForm.message}
                onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                required
                maxLength={500}
                rows={5}
                className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 placeholder:text-foreground-400 resize-none"
                placeholder="请输入您的留言（不超过500字）"
              ></textarea>
              <p className="text-xs text-foreground-400 mt-1 text-right">{contactForm.message.length}/500</p>
            </div>
            <button
              type="submit"
              className="w-full px-6 py-3 bg-primary-500 text-white rounded-lg font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              发送留言
            </button>
            {formStatus !== 'idle' && (
              <p className={`mt-3 text-sm text-center ${formStatus === 'success' ? 'text-green-600' : 'text-red-500'}`}>
                {formMessage}
              </p>
            )}
          </form>
        </div>
      </section>
    </main>
  );
}